import { useState } from 'react';
import { downloadCsv, useBalanceSheet } from '../hooks/useReports';
import PrintButton from '../components/PrintButton';

export default function BalanceSheetPage() {
  const currentYear = new Date().getFullYear();
  const [fiscalYear, setFiscalYear] = useState(currentYear);
  const [asOf, setAsOf] = useState(`${currentYear}-12-31`);
  const [applied, setApplied] = useState<{ fiscalYear: number; asOf: string } | null>({
    fiscalYear: currentYear,
    asOf: `${currentYear}-12-31`,
  });

  const { data, isLoading, error } = useBalanceSheet(applied?.fiscalYear, applied?.asOf);

  return (
    <div>
      <div className="page-header">
        <h2>貸借対照表</h2>
      </div>
      {error && <div className="error-banner">{(error as Error).message}</div>}

      <div className="card">
        <div className="toolbar">
          <label>
            事業年度{' '}
            <input
              type="number"
              value={fiscalYear}
              onChange={(e) => setFiscalYear(Number(e.target.value))}
              style={{ width: 100 }}
            />
          </label>
          <label>
            時点{' '}
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </label>
          <button className="primary" onClick={() => setApplied({ fiscalYear, asOf })}>
            表示
          </button>
          <button
            onClick={() =>
              applied && downloadCsv('/api/reports/balance-sheet', applied)
            }
            disabled={!applied}
          >
            CSV出力
          </button>
          <PrintButton />
        </div>
      </div>

      {isLoading && <div>読み込み中...</div>}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div className="card"><h3 style={{ margin: 0 }}>資産</h3></div>
            <table>
              <thead>
                <tr>
                  <th>勘定科目</th>
                  <th className="numeric" style={{ width: 130 }}>残高</th>
                </tr>
              </thead>
              <tbody>
                {data.assets.map((a) => (
                  <tr key={a.accountId}>
                    <td>{a.code} {a.name}</td>
                    <td className="numeric">{a.balance.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>資産合計</th>
                  <th className="numeric">{data.totalAssets.toLocaleString()}</th>
                </tr>
              </tfoot>
            </table>
          </div>

          <div>
            <div className="card"><h3 style={{ margin: 0 }}>負債</h3></div>
            <table>
              <thead>
                <tr>
                  <th>勘定科目</th>
                  <th className="numeric" style={{ width: 130 }}>残高</th>
                </tr>
              </thead>
              <tbody>
                {data.liabilities.map((l) => (
                  <tr key={l.accountId}>
                    <td>{l.code} {l.name}</td>
                    <td className="numeric">{l.balance.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>負債合計</th>
                  <th className="numeric">{data.totalLiabilities.toLocaleString()}</th>
                </tr>
              </tfoot>
            </table>

            <div className="card" style={{ marginTop: 16 }}><h3 style={{ margin: 0 }}>純資産</h3></div>
            <table>
              <thead>
                <tr>
                  <th>勘定科目</th>
                  <th className="numeric" style={{ width: 130 }}>残高</th>
                </tr>
              </thead>
              <tbody>
                {data.equities.map((e) => (
                  <tr key={e.accountId}>
                    <td>{e.code} {e.name}</td>
                    <td className="numeric">{e.balance.toLocaleString()}</td>
                  </tr>
                ))}
                <tr>
                  <td className="text-muted">当期純損益</td>
                  <td className="numeric">{data.netIncome.toLocaleString()}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th>純資産合計</th>
                  <th className="numeric">{data.totalEquity.toLocaleString()}</th>
                </tr>
                <tr>
                  <th>負債純資産合計</th>
                  <th
                    className="numeric"
                    style={{ color: data.balanced ? 'var(--color-success)' : 'var(--color-danger)' }}
                  >
                    {(data.totalLiabilities + data.totalEquity).toLocaleString()}{' '}
                    {data.balanced ? '✓' : '⚠'}
                  </th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
